import { clsx } from "clsx";
import { darken, saturate } from "polished";
import { type CSSProperties, type FC, forwardRef, type HTMLAttributes, type PropsWithChildren, useMemo } from "react";

import { getCssColor } from "../../utils/color/getCssColor.ts";
import style from "./ColoredLabel.module.css";

export interface ColoredLabelProps {
  label?: string;
  color: CSSProperties["backgroundColor"];
  domProps?: HTMLAttributes<HTMLElement>;
  style?: CSSProperties;
}

export interface ColoredBulletProps {
  color: CSSProperties["backgroundColor"];
  className?: string;
  style?: CSSProperties;
}

const colorVariableRegex = /^var\(([a-z0-9-]+)\)$/;

export const ColoredBullet: FC<ColoredBulletProps> = (props) => {
  const bgColor = useMemo(() => {
    if (!props.color) return undefined;

    let bgColor = props.color;

    if (colorVariableRegex.test(bgColor)) {
      const variable = colorVariableRegex.exec(bgColor)?.[1];
      if (variable) {
        bgColor = getComputedStyle(document.documentElement).getPropertyValue(variable)?.trim();
      }
    }

    return getCssColor(bgColor);
  }, [props.color]);
  const borderColor = useMemo(() => (bgColor ? darken(0.15, saturate(0.1, bgColor)) : "#B6BABF"), [bgColor]);

  return (
    <span
      aria-hidden
      className={clsx(style.mini_bullet, props.className, { [style.no_color]: !bgColor })}
      style={{ ...props.style, backgroundColor: bgColor, borderColor: bgColor ? borderColor : undefined }}
      data-testid="colored-bullet"
    />
  );
};

export const ColoredLabel = forwardRef<HTMLSpanElement, PropsWithChildren<ColoredLabelProps>>(
  function ColoredLabel(props, ref) {
    return (
      <span
        className={style.colored_label}
        {...props.domProps}
        ref={ref}
        data-testid="colored-label"
        style={props.style}
      >
        <ColoredBullet color={props.color} />
        {props.label || props.children}
      </span>
    );
  },
);
